import { Link, router } from "@inertiajs/react"
import { CheckCircle2, UserPlus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { Button } from "@/components/ui/button"
import { candidatesPath } from "@/routes"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-706 — Candidates-to-review: the first consumer of the reusable
 * {@link HomeTriageSection} shell (OKS-707/708 build sibling consumers on
 * the same shell). Wires the candidate card grid + the cold-start / empty
 * body slots and owns the Retry round-trip.
 *
 * Mounted inside an Inertia `<Deferred data="candidates_to_review">` on
 * `Brand/Home`, so `section` is always resolved here; the deferred-pending
 * first paint renders the shell's `loading` skeleton via the `<Deferred>`
 * fallback. Retry re-fetches ONLY this prop (`router.reload`) and flips a
 * local `loading` state so the skeleton shows immediately on click.
 */
export interface CandidatesToReviewSectionProps {
  section: SectionData
}

export function CandidatesToReviewSection({
  section,
}: CandidatesToReviewSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["candidates_to_review"],
      onFinish: () => setIsRetrying(false),
    })
  }

  const state = isRetrying ? "loading" : section.state

  return (
    <HomeTriageSection
      title={t("candidates.home_review.title")}
      state={state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="candidates-to-review"
      // OKS-953 (PRD R7) — one of the FIVE candidate segments that collapse.
      collapseWhenEmpty
      emptyTestId="candidates-to-review-empty"
      renderColdStart={() => (
        <EmptyState
          icon={UserPlus}
          title={t("candidates.home_review.cold_start.title")}
          titleAs="h2"
          description={t("candidates.home_review.cold_start.description")}
          data-testid="candidates-to-review-cold-start"
          actions={
            <Button asChild>
              <Link href={candidatesPath({ add: "1" })}>
                {t("candidates.home_review.cold_start.cta")}
              </Link>
            </Button>
          }
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCircle2}
          title={t("candidates.home_review.empty.title")}
          titleAs="h2"
          description={t("candidates.home_review.empty.description")}
          data-testid="candidates-to-review-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
